import { useState } from "react";
import { Mail, ShieldAlert, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageParts";
import { SITE } from "../config/site";

const SUBJECTS = [
  "General enquiry",
  "Master Trading",
  "Strategy Trading",
  "Vault Trading",
  "Vault Yield",
];

const NEVER_ASK = [
  "Exchange API keys — with or without withdrawal permission",
  "Wallet private keys or seed phrases",
  "Financial account credentials",
  "Sensitive investment information",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: SUBJECTS[0], message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending || sent) return;
    if (form.name.trim().length < 2) return toast.error("Please enter your name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error("Please enter a valid email address.");
    if (form.message.trim().length < 10) return toast.error("Please add a few more words to your message.");

    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail || "Unable to send your enquiry.");
      }
      setSent(true);
      toast.success("Enquiry received — thank you. The team will respond by email.");
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact Us — Speak With the Team"
        path="/contact"
        description="Contact the Kalki AI Fund team with enquiries about master trading, strategy trading, vault trading or vault yield. Basic enquiries only — we never request sensitive credentials."
      />

      <PageHero
        eyebrow="Contact Us"
        titleLines={["Speak with", "the team."]}
        intro="Questions about any of the four offerings, custody arrangements or verification reports? Send a basic enquiry and the team will respond by email."
      />

      <section className="py-20 lg:py-24 border-t border-gold-400/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12">
            {/* Form */}
            <Reveal>
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="glass rounded-3xl p-7 sm:p-9 flex flex-col gap-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="cf-name" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                      Name
                    </label>
                    <input
                      id="cf-name"
                      data-testid="contact-name-input"
                      className="field"
                      placeholder="Your full name"
                      value={form.name}
                      onChange={set("name")}
                      maxLength={120}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="cf-email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                      Email
                    </label>
                    <input
                      id="cf-email"
                      data-testid="contact-email-input"
                      type="email"
                      className="field"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={set("email")}
                      maxLength={200}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="cf-subject" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                    Subject
                  </label>
                  <select
                    id="cf-subject"
                    data-testid="contact-subject-select"
                    className="field appearance-none"
                    value={form.subject}
                    onChange={set("subject")}
                  >
                    {SUBJECTS.map((s) => (
                      <option key={s} value={s} className="bg-ink-800">
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="cf-message" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                    Message
                  </label>
                  <textarea
                    id="cf-message"
                    data-testid="contact-message-input"
                    className="field min-h-[140px] resize-y"
                    placeholder="Your enquiry — keep it basic; never include credentials or sensitive information."
                    value={form.message}
                    onChange={set("message")}
                    maxLength={5000}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending || sent}
                  className="btn-gold mt-2 disabled:opacity-60 disabled:pointer-events-none w-full sm:w-fit"
                >
                  {sending ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </>
                  ) : sent ? (
                    <>
                      <CheckCircle2 size={16} /> Enquiry sent
                    </>
                  ) : (
                    "Send Enquiry"
                  )}
                </button>
                <p className="text-xs text-zinc-600 leading-relaxed">
                  This form collects only basic enquiry details (name, email, subject, message).
                  Do not include exchange API keys, wallet private keys, financial account
                  credentials or sensitive investment information.
                </p>
              </form>
            </Reveal>

            {/* Side panel */}
            <div className="flex flex-col gap-6">
              <Reveal delay={0.1}>
                <div className="glass-gold rounded-3xl p-7">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-400/80 mb-4">
                    Direct email
                  </p>
                  <a
                    href={`mailto:${SITE.contactEmail}`}
                    data-testid="contact-email-link"
                    className="flex items-center gap-3 text-gold-200 hover:text-gold-100 transition-colors"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-gold-400/30 bg-gold-400/[0.07]">
                      <Mail size={15} />
                    </span>
                    <span className="text-sm font-medium break-all">{SITE.contactEmail}</span>
                  </a>
                  <p className="mt-4 text-xs text-zinc-500 leading-relaxed">
                    Enquiries are reviewed and answered in order of receipt.
                  </p>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div
                  data-testid="contact-security-note"
                  className="rounded-3xl border border-crimson-400/25 bg-crimson-400/[0.05] p-7"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <ShieldAlert size={16} className="text-crimson-400" />
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-crimson-400">
                      We will never ask for
                    </p>
                  </div>
                  <ul className="flex flex-col gap-3">
                    {NEVER_ASK.map((t) => (
                      <li key={t} className="text-sm text-zinc-300 leading-relaxed flex gap-2.5">
                        <span className="mt-1.5 h-1.5 w-1.5 rotate-45 bg-crimson-400/70 shrink-0" />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}